import React from "react";
import { Pressable } from "react-native";
import { useUnistyles, UnistylesRuntime } from "react-native-unistyles";
import { View, Text, Button } from "@src/components";
import { Icon } from "@src/components/Icon";
import { Image } from "@src/components";
import type { VehicleDetection, SimilarFields } from "@visioni/a3s-vehicle-tracker";
import { formatVehicleType, buildSimilarFields } from "@visioni/a3s-vehicle-tracker";
import { formatCameraLabel } from "@src/utils/key-parser";
import { VehicleDetailPanel } from "./VehicleDetailPanel";
import { buildCardTitle, isKnown } from "./card-utils";

interface TimelineCardProps {
  vehicle: VehicleDetection;
  expandedEventId: string | null;
  onToggleExpanded: (id: string) => void;
  activeTimezone: string;
  actions: {
    onFindSimilar: (fields: SimilarFields) => void;
  };
}

export function TimelineCard({
  vehicle,
  expandedEventId,
  onToggleExpanded,
  activeTimezone,
  actions,
}: TimelineCardProps) {
  const vehicleKey = vehicle.id ?? `${vehicle.createdAt}-${vehicle.plateText}`;
  const isExpanded = expandedEventId === vehicleKey;
  const { theme } = useUnistyles();

  const cardTitle = buildCardTitle(vehicle);
  const subtitleParts = buildSubtitleParts(vehicle);
  const breakpoint = (UnistylesRuntime.breakpoint as string) ?? "md";
  const isSmall = breakpoint === "xs" || breakpoint === "sm" || breakpoint === "md";
  const thumbSize = isSmall ? { width: 72 } : { width: 98 };

  const accentColor = theme.colors.interactive.controls.selected_midground;

  return (
    <Pressable onPress={() => onToggleExpanded(vehicleKey)} style={isExpanded ? { zIndex: 10 } : undefined}>
      <View
        bg="surface"
        mb="xl"
        borderRadius={12}
        borderWidth={1}
        style={isExpanded ? { borderColor: accentColor, overflow: "visible" } : undefined}
        borderColor={isExpanded ? undefined : "border"}
      >
        <View flexDirection="row" alignItems="stretch" style={{ minHeight: 88 }}>
          {vehicle.imageUrls.length > 0 && (
            <View style={{ width: thumbSize.width, flexShrink: 0 }}>
              <Image
                source={{ uri: vehicle.imageUrls[0] }}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 11,
                }}
                contentFit="cover"
              />
            </View>
          )}

          <View style={{ flex: 1, minWidth: 0, padding: 8 }} flexDirection="row" alignItems="center" gap="sm">
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text textStyle="md/semibold" color="text" numberOfLines={1}>
                {cardTitle}
              </Text>
              {subtitleParts.plate && (
                <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                  {subtitleParts.plate}
                </Text>
              )}
              <View flexDirection="row" alignItems="center" gap="xs">
                <Text textStyle="xs/regular" color="textMuted" numberOfLines={1} style={{ flexShrink: 1 }}>
                  {subtitleParts.typeColorDoors}
                </Text>
                <Icon name={isExpanded ? "chevronUp" : "chevronDown"} size={10} color={theme.colors.textMuted} />
              </View>
              <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                {subtitleParts.camera}
              </Text>
            </View>

            <Pressable
              onPress={(e) => {
                e.stopPropagation();
              }}
              style={{ flexShrink: 0, marginRight: isSmall ? 0 : 100 }}
            >
              <Button
                variant="secondary"
                size="sm"
                label={isSmall ? "Similar" : "Find similar"}
                onPress={() => actions.onFindSimilar(buildSimilarFields(vehicle))}
              />
            </Pressable>
          </View>
        </View>

        {isExpanded && (
          <Pressable onPress={(e) => e.stopPropagation()} style={{ overflow: "visible" } as Record<string, string>}>
            <View style={{ paddingLeft: thumbSize.width + 8, paddingRight: 8, paddingBottom: 8 }}>
              <VehicleDetailPanel vehicle={vehicle} activeTimezone={activeTimezone} actions={actions} />
            </View>
          </Pressable>
        )}
      </View>
    </Pressable>
  );
}

function buildSubtitleParts(vehicle: VehicleDetection): {
  plate: string | null;
  typeColorDoors: string;
  camera: string;
} {
  const plate = isKnown(vehicle.plateText) ? vehicle.plateText : null;

  const parts: string[] = [];
  const type = formatVehicleType(vehicle.vehicleType);
  if (type.toLowerCase() !== "unknown") parts.push(type);
  if (isKnown(vehicle.color)) parts.push(vehicle.color!);
  const doors = vehicle.vehicleFeatures?.doors;
  if (doors && (typeof doors.val === "number" || typeof doors.val === "string")) {
    parts.push(`${doors.val} doors`);
  }
  const typeColorDoors = parts.length > 0 ? parts.join(" - ") : "Unknown";

  const camera = formatCameraLabel(`${vehicle.assetId}@${vehicle.cameraId}`);

  return { plate, typeColorDoors, camera };
}
